"use client";

import Link from "next/link";
import { LandingPageNavigation } from "@/components/ui/landing-page-navigation";
import { Users, ArrowRight, BarChart2, MessageCircle, Heart, Sparkles, Eye, Fingerprint } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { FullPageLoading } from "@/components/ui/loading";
import { useAuth } from "@/contexts/auth-context";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import MarketingCenteredBrandSearch from "./MarketingCenteredBrandSearch";
import { useTranslation } from "react-i18next";
import { TextLoop } from '@/components/motion-primitives/text-loop';
import { OrbitalBackground } from "@/components/landing/Backgrounds";
import { CoCreateAnimation, UnderstandDataAnimation, AlwaysThereAnimation, CreatorTrainingAnimation, VideoWatchAnimation, PersonalizedDataAnimation } from "./FeatureAnimations";
import { PricingSection, type BillingCycle } from "@/components/pricing/PricingSection";

const AI_MODELS = ["ChatGPT", "Claude", "Gemini"];

// AI Model Animation Component
function AIModelCycle() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % AI_MODELS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="relative inline-flex h-[1.1em] w-[7.5ch] items-baseline justify-center whitespace-nowrap">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={AI_MODELS[index]}
          className="absolute inset-0 flex items-center justify-center whitespace-nowrap leading-none tracking-[0em]"
          initial={{ opacity: 0, y: 12, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -12, filter: "blur(4px)" }}
          transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1], // Smooth easing
          }}
        >
          {AI_MODELS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}


export default function LandingPageClient() {
  const router = useRouter();
  const { user, isInitialized, isLoading, refreshUser } = useAuth(); // Use centralized auth context
  const { t } = useTranslation();
  const [isAuthenticating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  const handleLogin = () => {
    router.push('/auth/sign-in');
  };

  const handleSignUp = () => {
    router.push('/auth/sign-up');
  };

  const handlePlanSelect = (
    planId: string,
    buttonLink: string,
    requiresBillingSelection: boolean
  ) => {
    if (typeof window !== "undefined" && (planId === "pro" || planId === "pro-plus")) {
      setSelectedPlan(planId);
      const plan = planId === "pro" ? "pro" : "pro_plus";
      // Always route paid checkout through the dedicated /checkout flow.
      // This avoids stale in-memory auth state after bfcache/back navigation.
      router.push(`/checkout?plan=${plan}`);
      return;
    }

    const destination =
      requiresBillingSelection && planId !== "free"
        ? `${buttonLink}?billing=${billingCycle}`
        : buttonLink;

    setSelectedPlan(planId);
    router.push(destination);
  };

  useEffect(() => {
    setSelectedPlan(null);
  }, [billingCycle]);

  // When the browser restores this page from bfcache (user pressed Back from Stripe),
  // refresh the auth session so server-side cookies are current before the next checkout.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setSelectedPlan(null);
        refreshUser().catch(() => {});
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [refreshUser]);

  // Show loading state only briefly, then render content
  if ((isLoading || !isInitialized) && !user) {
    return <FullPageLoading />;
  }

  // Disabled for local testing - allows viewing landing page while authenticated
  // Skip rendering if already authenticated and redirect is in progress
  // if (user) {
  //   return <FullPageLoading />;
  // }

  return (
    
    <div className="min-h-screen bg-background text-white justify-center">
      <OrbitalBackground />
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <LandingPageNavigation
          onGetStarted={handleSignUp}
          onSignIn={handleLogin}
          isAuthenticating={isAuthenticating}
        />
      </div>
      {/* Hero Section */}
      <section id="hero" className="min-h-[60vh] md:min-h-[65vh] lg:min-h-[70vh] w-full snap-start flex flex-col items-center justify-center px-6 pt-24 md:pt-28 relative">
        
        <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6 md:space-y-8 w-full -mt-6 md:-mt-8">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-normal leading-tight"
          >
            {t("landing.hero_title", "Lema: AI for Creators")}
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-3xl font-normal leading-tight text-center flex flex-col items-center">
            <span className="block whitespace-nowrap">
              {t("landing.hero_subtitle_prefix", "Creator Companion that")}
            </span>
            <span className="block mt-2 flex items-center justify-center min-w-[200px] md:min-w-[320px]">
              <TextLoop
                className="overflow-y-clip"
                interval={8}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 50,
                  mass: 5,
                }}
              >
                <span>{t("landing.hero_subtitle_cocreate", "co-creates with you, anytime")}</span>
                <span>{t("landing.hero_subtitle_analytics", "decodes your analytics")}</span>
                <span>{t("landing.hero_subtitle_available", "understand all of your content")}</span>
              </TextLoop>
            </span>
          </h2>
          <MarketingCenteredBrandSearch />
        </div>
      </section>

      {/* <section id="overview" className="w-full snap-start bg-background flex flex-col scroll-mt-32">
        <div className="w-full px-6 pt-6 md:pt-8 pb-12 md:pb-16">
          <div className="max-w-5xl mx-auto text-center">
            <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8 w-full">
              <div className="space-y-8">
                <h1 className="text-4xl md:text-5xl font-normal text-center">
                  {t("landing.overview_title", "Your AI Companion")}
                </h1>
                <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
                  {t(
                    "landing.overview_description",
                    "An AI that has watched all your content, is always available to co-create, and interprets your data so you don't have to."
                  )}
                </p>
                <div className="mt-6 md:mt-8 max-w-3xl mx-auto">
                  <ChatAnimation />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Available On Section */}


      {/* Features Section */}
      <section id="features" className="w-full snap-start bg-background flex flex-col scroll-mt-32">
        <div className="w-full px-6 flex-1 flex flex-col justify-center py-12">
          {/* Features */}
          <div className="space-y-6 md:space-y-8 max-w-4xl mx-auto text-center w-full">
            <h1 className="text-4xl md:text-5xl font-normal text-center">
              {t("landing.features_title", "What Lema Does For You")}
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
              {t(
                "landing.features_subtitle",
                "Your AI companion, built for creators"
              )}
            </p>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mt-10">
              {[
                {
                  icon: <MessageCircle className="h-6 w-6 text-white" />,
                  title: t("landing.feature_cocreate_title", "Co-Create Together"),
                  description: t(
                    "landing.feature_cocreate_description",
                    "Brainstorm ideas, write scripts, and refine your creative vision with an AI that truly knows your work."
                  ),
                  animation: <CoCreateAnimation />,
                  link: "/auth/sign-up"
                },
                {
                  icon: <BarChart2 className="h-6 w-6 text-white" />,
                  title: t("landing.feature_understand_data_title", "Understand Your Data"),
                  description: t(
                    "landing.feature_understand_data_description",
                    "Figure out what works, what doesn't, and why — so you can make smarter content decisions."
                  ),
                  animation: <UnderstandDataAnimation />,
                  link: "/auth/sign-up"
                },
                {
                  icon: <Heart className="h-6 w-6 text-white" />,
                  title: t("landing.feature_always_there_title", "Be There For Your Journey"),
                  description: t(
                    "landing.feature_always_there_description",
                    "Need a second opinion? Editing feeling rough? Lema is always available when you need it."
                  ),
                  animation: <AlwaysThereAnimation />,
                  link: "/auth/sign-up"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <GlassCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    animationSlot={feature.animation}
                    href={feature.link}
                  />
                </motion.div>
              ))}
            </div>
        </div>
      </section>

      {/* Why Lema Section */}
      <section id="why-lema" className="w-full snap-start bg-background flex flex-col scroll-mt-32">
        <div className="w-full px-6 flex-1 flex flex-col justify-center py-12">
          <div className="space-y-6 md:space-y-8 max-w-4xl mx-auto text-center w-full">
            <h1 className="text-4xl md:text-5xl font-normal text-center flex flex-col md:flex-row items-center justify-center gap-y-1 md:gap-y-0 md:gap-x-[0.375rem]">
              <span>{t("landing.vs_title_prefix", "Why Lema, Not")}</span>
              <span className="inline-flex items-center gap-x-[0.375rem] whitespace-nowrap">
                <AIModelCycle />
                <span>?</span>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
              {t("landing.vs_subtitle", "Most AI wasn't built for creators. Lema is.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mt-10">
            {[
              {
                icon: <Sparkles className="h-6 w-6 text-white" />,
                title: t("landing.vs_creators_title", "Trained on Top Creators"),
                description: t(
                  "landing.vs_creators_description",
                  "Lema has studied how high-engagement creators consistently reach their audiences — turning real creator data into patterns, not generic web content."
                ),
                animation: <CreatorTrainingAnimation />,
                link: "/auth/sign-up"
              },
              {
                icon: <Eye className="h-6 w-6 text-white" />,
                title: t("landing.vs_video_title", "Actually Watches Videos"),
                description: t(
                  "landing.vs_video_description",
                  "Paste a video link and Lema genuinely watches it. Other AI tools often can't and just make things up."
                ),
                animation: <VideoWatchAnimation />,
                link: "/auth/sign-up"
              },
              {
                icon: <Fingerprint className="h-6 w-6 text-white" />,
                title: t("landing.vs_personal_title", "Knows Your Account"),
                description: t(
                  "landing.vs_personal_description",
                  "Lema connects to your data and advises you based on what you've actually posted. No hollow tips like \"post more\" or \"follow trends\"."
                ),
                animation: <PersonalizedDataAnimation />,
                link: "/auth/sign-up"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                  animationSlot={item.animation}
                  href={item.link}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="Platforms" className="w-full snap-start bg-background flex flex-col scroll-mt-32">
        <div className="w-full px-6 py-12">
          <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
            <h1 className="text-4xl md:text-5xl font-normal text-center">
              {t("landing.platforms_title", "We Serve Creators on")}
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
              {t("landing.platforms_subtitle", "Instagram, TikTok, and YouTube")}
            </p>
          </div>

            <div className="mt-10 grid grid-cols-3 gap-6 md:gap-10 items-center justify-items-center">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                aria-label="Instagram"
              >
                <div className="text-center flex flex-col items-center justify-center">
                  <FaInstagram className="mx-auto h-14 w-14 md:h-16 md:w-16" />
                  <span className="mt-3 text-lg">Instagram</span>
                </div>
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                aria-label="TikTok"
              >
                <div className="text-center flex flex-col items-center justify-center">
                  <SiTiktok className="mx-auto h-14 w-14 md:h-16 md:w-16" />
                  <span className="mt-3 text-lg">TikTok</span>
                </div>
              </a>

              {/* YouTube */}
              <a
                href="https://www.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                aria-label="YouTube"
              >
                <div className="text-center flex flex-col items-center justify-center">
                  <FaYoutube className="mx-auto h-14 w-14 md:h-16 md:w-16" />
                  <span className="mt-3 text-lg">YouTube</span>
                </div>
              </a>
            </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="w-full snap-start bg-background flex flex-col scroll-mt-32">
        <div className="w-full px-6 flex-1 flex flex-col justify-center py-12">
          <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
            <h1 className="text-4xl md:text-5xl font-normal text-center">
              {t("landing.pricing_title", "Pricing")}
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
              {t("landing.pricing_subtitle", "Choose the plan that's right for you.")}
            </p>
          </div>
          <div className="w-full mt-12 md:mt-16">
            <PricingSection
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              onPlanSelect={handlePlanSelect}
              selectedPlan={selectedPlan}
            />
          </div>
        </div>
      </section>

      {/* Available On Section */}

      <section id="about" className="w-full snap-start bg-background flex flex-col scroll-mt-32 mb-24">
        <div className="w-full px-6 py-12">

          <h1 className="text-4xl md:text-5xl font-normal mb-6 text-center">
            {t("landing.about_title", "About Us")}
          </h1>

            <GlassPanel className="p-4 md:p-6 space-y-6">

              <div className="space-y-3 text-center">
                <h3 className="text-2xl md:text-3xl text-neutral-200 font-normal">
                  {t("landing.about_team_title", "Our Team")}
                </h3>
                <p className="text-neutral-300">
                  {t(
                    "landing.about_team_body",
                    "We are a team of two founders (ex-Meta content & ML team, Oxford + Huawei Research)."
                  )}
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <div className="inline-flex items-center px-4 rounded-lg bg-white/5 border border-white/10">
                    <Users className="w-4 h-4 mr-2" />
                    <span className="mr-3">Rivaldo</span>
                    <Link
                      href="https://www.linkedin.com/in/rivaldophilip"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 transition-colors"
                    >
                      LinkedIn <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                  <div className="inline-flex items-center px-3 rounded-lg bg-white/5 border border-white/10">
                    <Users className="w-4 h-4 mr-2" />
                    <span className="mr-3">Yi Yao</span>
                    <Link
                      href="https://www.linkedin.com/in/yi-yao-t-9719301a3/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 transition-colors"
                    >
                      LinkedIn <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                    <Link
                      href="https://yiyaotan-site.vercel.app/work/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 transition-colors px-2"
                    >
                      Website <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* <div className="space-y-3">
                <h3 className="text-2xl md:text-3xl text-neutral-200 font-normal">
                  {t("landing.about_name_origin_title", "Name Origin")}
                </h3>
                <p className="text-neutral-300 leading-relaxed">
                  {t(
                    "landing.about_name_origin_body",
                    "Lema is named after the mathematical term 'lemma'—a small theorem used to prove a larger one. We provide the tools for creators to prove they can make a living from their passion."
                  )}
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl md:text-3xl text-neutral-200 font-normal">
                  {t("landing.about_impact_title", "Impact")}
                </h3>
                <div className="space-y-3">
                  {[
                    { label: t("landing.stat_founded", "Founded"), value: "2025" },
                    { label: t("landing.stat_creators_served", "Creators Served"), value: "10" },
                    { label: t("landing.stat_brand_deals_closed", "Brand Deals Closed"), value: "3" },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                      viewport={{ once: true }}
                      className="flex justify-between items-center py-3 px-4 rounded-lg bg-white/5 border border-white/10"
                    >
                      <span className="text-neutral-400">{stat.label}</span>
                      <span className="font-medium">{stat.value}</span>
                    </motion.div>
                  ))}
                </div>
              </div> */}

              <div className="space-y-4 text-center flex flex-col items-center">
                <p className="text-neutral-300 leading-relaxed">
                  {t(
                    "landing.about_support_body",
                    "We love content creation and would like to support your content creator journey"
                  )}
                </p>
                <Link
                  href="https://calendar.app.google/RTSVyDNERnvhoeuM7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/30 hover:text-cyan-200 transition-colors font-medium"
                >
                  {t("landing.about_talk_button", "Talk directly with us")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassPanel>
        </div>
      </section>

    </div>
  );
}
